import { Select, Tooltip, message } from "antd";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { setPrecedenceTierRequest } from "store/goldenNominal/actions";

// PRE-2379 — a dataset's source-type precedence tier (authority below GOLDEN_NOMINAL).
// The golden tier itself is excluded: it moves only via nominate/revoke, so the picker
// is hidden while the dataset is the golden nominal.

const TIER_OPTIONS = [
    { value: "MANUAL_ENTRY", label: "Manual entry" },
    { value: "API_SYNC", label: "API sync" },
    { value: "PROMOTED_CSV", label: "Promoted CSV" },
];

interface IDatasetPrecedenceTierSelectProps {
    businessAreaId?: string;
    schemaId?: string;
    dataSetId?: string;
    precedenceTier?: string | null;
    isGoldenNominal?: boolean;
    canEdit?: boolean;
}

export default function DatasetPrecedenceTierSelect({
    businessAreaId,
    schemaId,
    dataSetId,
    precedenceTier,
    isGoldenNominal,
    canEdit,
}: IDatasetPrecedenceTierSelectProps) {
    const dispatch = useAppDispatch();
    const goldenBySchema = useAppSelector((s) => s.goldenNominal?.goldenBySchema);
    const tierOverride = useAppSelector((s) => s.goldenNominal?.tierByDataSet?.[dataSetId || ""]);
    const setTierResult = useAppSelector((s) => s.goldenNominal?.setTierResult);
    const pendingRef = React.useRef(false);

    const effectiveTier = tierOverride !== undefined ? tierOverride : precedenceTier || null;

    React.useEffect(() => {
        if (pendingRef.current && setTierResult) {
            pendingRef.current = false;
            if (setTierResult.success) {
                message.success("Source tier updated.");
            } else {
                message.error(setTierResult.message || "Failed to update the source tier.");
            }
        }
    }, [setTierResult]);

    // Session-aware golden check: a nominate/revoke done this session must hide/show the
    // picker immediately (the raw prop only reflects the last fetch).
    if (!dataSetId || isDatasetGolden(goldenBySchema, schemaId, dataSetId, isGoldenNominal)) {
        // The golden nominal's tier is GOLDEN_NOMINAL by definition — revoke first to re-tier.
        return null;
    }

    return (
        <Tooltip title="Source-type authority tier — feeds default trust in dedup survivorship">
            <Select
                // Keeps its width in the dataset's button bar when the record panel narrows it.
                className="min-w-44 shrink-0"
                allowClear
                placeholder="Source tier: unranked"
                disabled={!canEdit}
                value={effectiveTier || undefined}
                options={TIER_OPTIONS}
                onChange={(value) => {
                    pendingRef.current = true;
                    dispatch(setPrecedenceTierRequest(businessAreaId, schemaId, dataSetId, value || null));
                }}
                data-testid="dataset-precedence-tier-select"
            />
        </Tooltip>
    );
}
